/**
 * usePageTracking.js
 *
 * Sistema de tracking de visitas web con dos capas:
 *
 * 1. GLOBAL (router-level) — sendRouterPageView():
 *    Se llama desde router.afterEach para contar TODA visita (enter).
 *    No depende de que un componente se monte correctamente.
 *
 * 2. COMPONENTE (composable) — usePageTracking():
 *    Se llama desde el <script setup> de cada página.
 *    Envía engagement al salir (exit): tiempo, scroll, rebote.
 *    NO envía enter (para no duplicar con el router).
 *
 * Uso:
 *   // En router.js:
 *   import { sendRouterPageView } from '@/composables/usePageTracking'
 *   router.afterEach((to) => sendRouterPageView(to))
 *
 *   // En componentes (opcional, para engagement):
 *   usePageTracking('search', { city: () => route.params.city })
 */

import { onMounted, onUnmounted } from 'vue'

const ENDPOINT = '/api/analytics/page-event'

const BOUNCE_MAX_TIME_MS = 15_000
const BOUNCE_MAX_SCROLL_PCT = 25

function resolveOption(opt, fallback = null) {
  if (typeof opt === 'function') return opt() ?? fallback
  return opt ?? fallback
}

function postPayload(payload) {
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => { /* non-blocking analytics */ })
}

function sendBeaconPayload(payload) {
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return true
  } catch { /* fallback */ }
  postPayload(payload)
  return true
}

/* ────────────────────────────────────────────────────────────────────────────
 * CAPA 1: Tracking global desde el router (enter / view count)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Determina el pageType y contexto a partir de un route object de vue-router.
 * @returns {{ pageType: string, context: object } | null}
 */
function resolvePageInfo(to) {
  const path = to.path

  // Landing principal
  if (path === '/') {
    return { pageType: 'landing', context: {} }
  }

  // Buscador de colivings (ES + EN, todas las variantes)
  if (path.startsWith('/encuentratucoliving') || path.startsWith('/findyourcoliving')) {
    return {
      pageType: 'search',
      context: { city: to.params.city || to.params.segment || null },
    }
  }

  // Índice del blog
  if (to.name === 'BlogPage' || path === '/blog') {
    return {
      pageType: 'blog',
      context: { blogSlug: '_blog_index', blogTitle: 'Blog' },
    }
  }

  // Post de blog (catch-all /:slug)
  if (to.name === 'BlogPostView') {
    return {
      pageType: 'blog',
      context: { blogSlug: to.params.slug || null },
    }
  }

  // Ficha de coliving
  if (path.startsWith('/colivings/') && to.params.id) {
    return {
      pageType: 'coliving_detail',
      context: { colivingId: to.params.id },
    }
  }

  return null
}

/**
 * Envía un evento enter (vista) desde router.afterEach.
 * Llamar directamente: router.afterEach((to) => sendRouterPageView(to))
 */
export function sendRouterPageView(to) {
  // No trackear en SSR / prerender
  if (typeof window === 'undefined') return
  // No trackear rutas privadas / admin
  if (to.meta?.requiresAuth || to.meta?.noIndex) return

  const info = resolvePageInfo(to)
  if (!info) return

  postPayload({
    pageType: info.pageType,
    phase: 'enter',
    timeMs: 0,
    scrollPct: 0,
    isBounce: false,
    ...info.context,
  })
}

/* ────────────────────────────────────────────────────────────────────────────
 * CAPA 2: Composable por componente (exit / engagement)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * @param {'landing' | 'coliving_detail' | 'search' | 'blog'} pageType
 */
export function usePageTracking(pageType, options = {}) {
  const startTime = Date.now()
  let maxScrollPct = 0
  let hasSentExit = false

  function buildContextFields() {
    return {
      colivingId: resolveOption(options.colivingId),
      colivingTitle: resolveOption(options.colivingTitle),
      fromSearch: resolveOption(options.fromSearch, false),
      city: resolveOption(options.city),
      blogSlug: resolveOption(options.blogSlug),
      blogTitle: resolveOption(options.blogTitle),
    }
  }

  function sendExit() {
    if (hasSentExit) return
    hasSentExit = true

    const timeMs = Date.now() - startTime
    const scrollPct = maxScrollPct
    const isBounce =
      pageType === 'landing' &&
      timeMs < BOUNCE_MAX_TIME_MS &&
      scrollPct < BOUNCE_MAX_SCROLL_PCT

    sendBeaconPayload({
      pageType,
      phase: 'exit',
      timeMs,
      scrollPct,
      isBounce,
      ...buildContextFields(),
    })
  }

  function updateScroll() {
    const scrolled = window.scrollY + window.innerHeight
    const total = Math.max(document.documentElement.scrollHeight, 1)
    const pct = Math.min(100, Math.round((scrolled / total) * 100))
    if (pct > maxScrollPct) maxScrollPct = pct
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') sendExit()
  }

  onMounted(() => {
    window.addEventListener('scroll', updateScroll, { passive: true })
    updateScroll()
    document.addEventListener('visibilitychange', onVisibilityChange)
    // NO envía enter: el router ya lo hizo via sendRouterPageView
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', updateScroll)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    sendExit()
  })
}
